import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import { Clock, ShieldAlert, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { computeLoginRedirectTarget } from "@/lib/loginRedirect";
import {
  WARN_BEFORE_MS,
  SESSION_LOST_EVENT,
  getToken,
  msUntilExpiry,
  renewSession,
} from "@/lib/session";

/**
 * SessionExpiryManager — mounted once at app root (inside the router).
 *
 * Watches the login token's built-in `exp` and, instead of letting the page
 * silently fail once the session lapses, shows a friendly centered prompt:
 *   • "warning" (5 min before expiry, token still valid) → the user can
 *     "Stay signed in" which renews the session IN PLACE (no re-login).
 *   • "lost" (at/after expiry, or a real 401 from any API call) → the user
 *     must "Sign back in"; renewing an expired token is intentionally not
 *     allowed (it would defeat session expiry).
 * A reactive `compass:session-lost` window event (fired by the axios
 * interceptor on any 401) is the backstop for the proactive timer.
 * The prompt overlays the current page — unsaved work stays on screen.
 */

// Stay quiet this long after the user dismisses the "expired" prompt before a
// fresh 401 is allowed to reopen it (avoids spam on polling pages).
const DISMISS_COOLDOWN_MS = 30 * 1000;

export default function SessionExpiryManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState(null); // null | "warning" | "lost"
  const [busy, setBusy] = useState(false);
  const warnTimer = useRef(null);
  const lostTimer = useRef(null);
  const suppressUntil = useRef(0);
  // Whether this page has EVER seen the token valid during its lifetime.
  // The "expired" modal is only for a session that lapses WHILE the page is
  // open — so a token that was already expired at load (cold revisit, or a
  // stale token lingering on a public page) must never trip it. This is a
  // ref (page-lifetime memory) that naturally resets on a full reload.
  const observedValid = useRef(false);

  const onLoginPage = location.pathname.startsWith("/login");

  const clearTimers = useCallback(() => {
    if (warnTimer.current) {
      clearTimeout(warnTimer.current);
      warnTimer.current = null;
    }
    if (lostTimer.current) {
      clearTimeout(lostTimer.current);
      lostTimer.current = null;
    }
  }, []);

  const schedule = useCallback(() => {
    clearTimers();
    if (!getToken()) {
      setMode(null); // logged out → never prompt
      return;
    }
    const ms = msUntilExpiry();
    if (ms == null) return; // opaque token → rely on the 401 backstop only
    if (ms <= 0) {
      // Already expired. Only prompt if we saw this session ALIVE while the
      // page was open (it lapsed in front of the user). A token that was
      // already expired at load must NOT pop the modal — ProtectedRoute
      // bounces cold revisits to /login instead.
      if (observedValid.current) setMode("lost");
      return;
    }
    observedValid.current = true; // token is currently valid → session alive
    lostTimer.current = setTimeout(() => setMode("lost"), ms);
    const warnIn = ms - WARN_BEFORE_MS;
    if (warnIn <= 0) {
      setMode((m) => (m ? m : "warning"));
    } else {
      warnTimer.current = setTimeout(
        () => setMode((m) => (m ? m : "warning")),
        warnIn,
      );
    }
  }, [clearTimers]);

  // (Re)schedule on mount and on every navigation — this also picks up a
  // fresh token after a same-tab login (no storage event fires for our tab).
  useEffect(() => {
    schedule();
    return clearTimers;
  }, [schedule, clearTimers, location.pathname]);

  // Reactive backstop: a 401 from any axios call dispatches this event.
  useEffect(() => {
    const onLost = () => {
      if (Date.now() < suppressUntil.current) return;
      if (!getToken()) return;
      // Ignore a 401 when we never saw a live session this page lifetime —
      // that's a cold revisit with a stale token, handled by a login bounce.
      if (!observedValid.current) return;
      setMode("lost");
    };
    window.addEventListener(SESSION_LOST_EVENT, onLost);
    return () => window.removeEventListener(SESSION_LOST_EVENT, onLost);
  }, []);

  // Cross-tab sync: another tab renewed (new token) or logged out (removed).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== "compass_token") return;
      if (!e.newValue) {
        observedValid.current = false;
        setMode(null);
        return;
      }
      // A fresh token was written by a login/renew in another tab —
      // re-baseline this page's "seen alive" state and reschedule.
      observedValid.current = false;
      setMode(null);
      schedule();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [schedule]);

  // setTimeout drifts while the machine sleeps, so re-evaluate against the
  // wall clock whenever the tab regains focus.
  useEffect(() => {
    const recheck = () => {
      if (document.visibilityState === "hidden") return;
      if (!getToken()) return;
      const ms = msUntilExpiry();
      if (ms == null) return;
      if (ms <= 0) {
        // Expired since we last looked. Only prompt if the session was
        // alive while this page was open (e.g. tab backgrounded for hours,
        // then refocused) — never for a stale token from a cold load.
        if (observedValid.current) setMode("lost");
      } else {
        observedValid.current = true;
        if (ms <= WARN_BEFORE_MS) setMode((m) => (m ? m : "warning"));
        else schedule();
      }
    };
    window.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => {
      window.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("focus", recheck);
    };
  }, [schedule]);

  const handleRenew = async () => {
    setBusy(true);
    try {
      await renewSession();
      toast.success("You're still signed in — your session was extended.");
      setMode(null);
      schedule();
    } catch {
      // Token already expired / renew refused → must sign in again.
      setMode("lost");
    } finally {
      setBusy(false);
    }
  };

  const handleRelogin = () => {
    const dest = `${location.pathname}${location.search}`;
    setMode(null);
    navigate(computeLoginRedirectTarget(dest));
  };

  const handleDismiss = () => {
    if (mode === "lost") {
      suppressUntil.current = Date.now() + DISMISS_COOLDOWN_MS;
    }
    setMode(null);
  };

  if (!mode || onLoginPage) return null;
  const warning = mode === "warning";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      data-testid="session-expiry-modal"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-expiry-title"
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/10 overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
                warning ? "bg-amber-100 text-amber-600" : "bg-rose-100 text-rose-600"
              }`}
            >
              {warning ? <Clock size={22} /> : <ShieldAlert size={22} />}
            </div>
            <div className="min-w-0">
              <h2
                id="session-expiry-title"
                data-testid="session-expiry-title"
                className="text-lg font-semibold text-slate-900"
              >
                {warning ? "Your session is about to expire" : "Your session has expired"}
              </h2>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600" data-testid="session-expiry-message">
                {warning ? (
                  <>
                    You&apos;ll be signed out in about 5 minutes for security.
                    Choose <span className="font-medium text-slate-800">Stay signed in</span> to
                    keep working — your unsaved changes stay on screen.
                  </>
                ) : (
                  <>
                    For your security you&apos;ve been signed out. Please sign back
                    in to continue saving and loading your work — anything on
                    screen right now stays put until you do.
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5">
            <Button
              variant="ghost"
              onClick={handleDismiss}
              disabled={busy}
              data-testid="session-dismiss-btn"
              className="text-slate-600 hover:text-slate-900"
            >
              Not now
            </Button>
            {warning ? (
              <Button
                onClick={handleRenew}
                disabled={busy}
                data-testid="session-renew-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                {busy ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" /> Renewing…
                  </>
                ) : (
                  "Stay signed in"
                )}
              </Button>
            ) : (
              <Button
                onClick={handleRelogin}
                data-testid="session-relogin-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                Sign back in
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
